import playerProfileImg from '../../images/player-profile.png';
import { PLAYER_BASE_URL } from '../../apiConfig';
import { useState, useEffect } from 'react';
import { Shield, ExternalLink, Calendar, Hash, Target, User, Globe, Home, Plane, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';

function PlayerProfileHeader({ data, teamName }) {
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const isTablet = windowWidth <= 1200 && windowWidth > 1024;
    const isMobile = windowWidth <= 1024;
    const accentYellow = '#E7C424';

    const styles = {
        wrapper: {
            width: '100%',
            background: 'linear-gradient(145deg, #0A1172 0%, #040960 100%)',
            padding: isMobile ? '32px 20px' : '48px',
            boxSizing: 'border-box',
            color: '#FFFFFF',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            gap: '32px',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
        },
        topSection: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'stretch',
            flexDirection: isTablet || isMobile ? 'column' : 'row',
            gap: '40px',
            position: 'relative',
            zIndex: 2,
        },
        leftCol: {
            display: 'flex',
            flexDirection: 'column',
            gap: '32px',
            flex: 1.2,
        },
        teamBadge: {
            background: 'rgba(231,196,36,0.1)',
            border: `1px solid ${accentYellow}`,
            borderRadius: '4px',
            padding: '6px 16px',
            width: 'fit-content',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
        },
        teamText: {
            fontFamily: 'var(--fontDisplay)',
            fontWeight: 800,
            fontSize: '14px',
            color: accentYellow,
            margin: 0,
            textTransform: 'uppercase',
            letterSpacing: '1px',
        },
        playerMain: {
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '24px' : '40px',
            flexDirection: isMobile ? 'column' : 'row',
        },
        imageFrame: {
            width: isMobile ? '160px' : '200px',
            height: isMobile ? '160px' : '200px',
            border: `4px solid #FFFFFF`,
            borderRadius: '50%',
            overflow: 'hidden',
            background: '#FFFFFF',
            flexShrink: 0,
            boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
        },
        nameBox: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            textAlign: isMobile ? 'center' : 'left',
        },
        firstName: {
            fontFamily: 'var(--fontDisplay)',
            fontSize: isMobile ? '48px' : '82px',
            lineHeight: 0.85,
            margin: 0,
            textTransform: 'uppercase',
            fontWeight: 900,
            letterSpacing: '-2px',
        },
        lastName: {
            fontFamily: 'var(--fontBody)',
            fontSize: isMobile ? '20px' : '28px',
            fontWeight: 700,
            margin: '8px 0 0 0',
            opacity: 0.7,
            textTransform: 'uppercase',
            letterSpacing: '2px',
        },
        rightCol: {
            display: 'flex',
            flexDirection: 'column',
            gap: '32px',
            flex: 1,
            justifyContent: 'space-between',
            alignItems: isTablet || isMobile ? 'center' : 'flex-end',
        },
        infoGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '12px',
            width: '100%',
            maxWidth: isMobile ? '100%' : '440px',
        },
        infoBox: {
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.1)',
            padding: '12px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            borderRadius: '4px',
        },
        infoLabel: {
            fontFamily: 'var(--fontDisplay)',
            fontSize: '11px',
            fontWeight: 800,
            color: accentYellow,
            textTransform: 'uppercase',
            letterSpacing: '1px',
        },
        infoValue: {
            fontFamily: 'var(--fontBody)',
            fontSize: '15px',
            fontWeight: 700,
        },
        statsRow: {
            display: 'flex',
            gap: isMobile ? '20px' : '48px',
            background: 'rgba(0,0,0,0.2)',
            padding: '24px 32px',
            borderRadius: '8px',
            border: '1px solid rgba(255,255,255,0.05)',
            width: 'fit-content',
        },
        statItem: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
        },
        statValue: {
            fontFamily: 'var(--fontDisplay)',
            fontSize: isMobile ? '32px' : '44px',
            fontWeight: 900,
            lineHeight: 1,
            color: accentYellow,
        },
        statLabel: {
            fontFamily: 'var(--fontDisplay)',
            fontSize: '12px',
            fontWeight: 800,
            opacity: 0.6,
            textTransform: 'uppercase',
            letterSpacing: '1px',
            marginTop: '4px',
        },
        playCricketBtn: {
            background: accentYellow,
            padding: '12px 24px',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            textDecoration: 'none',
            color: '#0A1172',
            fontFamily: 'var(--fontDisplay)',
            fontWeight: 800,
            fontSize: '14px',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            transition: 'all 0.3s',
            width: 'fit-content',
            alignSelf: 'flex-start',
        }
    };

    const isOverseas = data.player_type?.toLowerCase().includes('overseas');
    const isHome = data.player_type?.toLowerCase().includes('home');
    const isVerified = data.documentVerification == 1;
    const iconColor = isVerified ? '#2ECC71' : accentYellow;

    let PlayerTypeIcon = <Shield size={14} color={iconColor} />;
    if (isHome) {
        PlayerTypeIcon = <Home size={14} color={iconColor} />;
    } else if (isOverseas) {
        PlayerTypeIcon = <Plane size={14} color={iconColor} />;
    }

    return (
        <div style={styles.wrapper}>
            {/* Decoration */}
            <div style={{
                position: 'absolute', top: -100, left: -100,
                width: '300px', height: '300px', background: 'rgba(231,196,36,0.03)',
                filter: 'blur(80px)', borderRadius: '50%'
            }} />

            <div style={styles.topSection}>
                <div style={styles.leftCol}>
                    <div style={styles.teamBadge}>
                        <Shield size={16} color={accentYellow} />
                        <p style={styles.teamText}>{teamName || data.team_name || 'CLUB NAME'}</p>
                    </div>
                    
                    <div style={styles.playerMain}>
                        <div style={styles.imageFrame}>
                            <img 
                                src={data.Photo ? `${PLAYER_BASE_URL}/${data.Photo}` : playerProfileImg} 
                                alt={data.Forename} 
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                            />
                        </div>
                        <div style={styles.nameBox}>
                            <h1 style={styles.firstName}>{data.Forename || 'PLAYER'}</h1>
                            <p style={styles.lastName}>{data.Surname || 'SURNAME'}</p>
                            
                            <div style={{ display: 'flex', gap: '16px', marginTop: '20px', justifyContent: isMobile ? 'center' : 'flex-start' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', opacity: 0.6 }}>
                                    <Hash size={14} />
                                    <span style={{ fontFamily: 'var(--fontBody)', fontSize: '13px', fontWeight: 600 }}>ID: {data.PlayerID}</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', opacity: 0.6 }}>
                                    <Globe size={14} />
                                    <span style={{ fontFamily: 'var(--fontBody)', fontSize: '13px', fontWeight: 600 }}>PC: {data.pc_player_id || 'N/A'}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {!isMobile && (
                        <motion.a 
                            whileHover={{ scale: 1.05, backgroundColor: '#FFFFFF' }}
                            href={`https://play-cricket.com/player_stats/player/${data.pc_player_id}`} 
                            target="_blank" rel="noreferrer" 
                            style={styles.playCricketBtn}
                        >
                            <span>Official Play Cricket Profile</span>
                            <ExternalLink size={16} />
                        </motion.a>
                    )}
                </div>

                <div style={styles.rightCol}>
                    <div style={styles.infoGrid}>
                        {[
                            { label: 'Debut', value: data.Debut, icon: <Calendar size={14} /> },
                            { label: 'Batting', value: data.BatStyle, icon: <Target size={14} /> },
                            { label: 'Bowling', value: data.BowlStyle, icon: <Target size={14} /> },
                            { label: 'Age Group', value: data.AgeGroup, icon: <User size={14} /> },
                            { label: 'Player Type', value: data.player_type, icon: PlayerTypeIcon, customColor: iconColor },
                            { label: 'Verified', value: isVerified ? 'Yes' : 'No', icon: <CheckCircle size={14} />, customColor: iconColor },
                        ].map((item, idx) => (
                            <div key={idx} style={styles.infoBox}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ color: item.customColor || accentYellow, display: 'flex', alignItems: 'center' }}>{item.icon}</span>
                                    <span style={styles.infoLabel}>{item.label}</span>
                                </div>
                                <span style={styles.infoValue}>{item.value || 'Not Added'}</span>
                            </div>
                        ))}
                    </div>

                    <div style={styles.statsRow}>
                        <div style={styles.statItem}>
                            <span style={styles.statValue}>{data.total_matchs || 0}</span>
                            <span style={styles.statLabel}>Matches</span>
                        </div>
                        <div style={{ width: '1px', background: 'rgba(255,255,255,0.1)', height: '100%' }} />
                        <div style={styles.statItem}>
                            <span style={styles.statValue}>{data.total_runs || 0}</span>
                            <span style={styles.statLabel}>Runs</span>
                        </div>
                        <div style={{ width: '1px', background: 'rgba(255,255,255,0.1)', height: '100%' }} />
                        <div style={styles.statItem}>
                            <span style={styles.statValue}>{data.total_wkts || 0}</span>
                            <span style={styles.statLabel}>Wickets</span>
                        </div>
                    </div>

                    {isMobile && (
                        <a 
                            href={`https://play-cricket.com/player_stats/player/${data.pc_player_id}`} 
                            target="_blank" rel="noreferrer" 
                            style={{...styles.playCricketBtn, width: '100%', justifyContent: 'center', alignSelf: 'stretch'}}
                        >
                            <span>Official Profile</span>
                            <ExternalLink size={16} />
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
}

export default PlayerProfileHeader;